import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, ListStage, StageLeadsData } from './crmApi';
import LeadPanel from './LeadPanel';
import { Empty, Head, LeadRow, Spinner, fmtWhen, inputCls, useCrmMe } from './ui';

// ── One kind of lead on its own list ─────────────────────────
// Poll Sent (the poll's status to update), Interested (waiting to pay), No
// Response (no reply to the poll; they stay with their salesperson), Cold
// Leads and Not Interested (who marked them, why and when), Junk Leads
// (nobody picked up after everyone's tries; open one to give it to someone).
// A salesperson sees their own; managers see everyone's and can pick one person.

const ABOUT: Record<ListStage, { title: string; sub: string; empty: string; icon: string }> = {
  'Poll Sent':      { title: 'Poll Sent', sub: 'Brochure and poll sent. Their status is due 3 hours after the poll went out.', empty: 'No polls waiting for a status', icon: '📊' },
  'Interested':     { title: 'Interested', sub: 'Said yes to the poll. Waiting for their payment, then Converted.', empty: 'Nobody waiting to pay', icon: '💰' },
  'No Response':    { title: 'No Response', sub: "No reply to the poll, didn't come for the visit, or no response after the P2P meeting. They stay with their salesperson: try again any time.", empty: 'Nobody has gone quiet', icon: '🔕' },
  'Cold Lead':      { title: 'Cold Leads', sub: 'Picked up but not a real lead: who marked them, why and when', empty: 'No cold leads', icon: '🧊' },
  'Not Interested': { title: 'Not Interested', sub: 'Said no: who, why and when', empty: 'Nobody said no', icon: '🙅' },
  'Junk Lead':      { title: 'Junk Leads', sub: "Not picked after every salesperson and Lead Manager tried 3 times, the phone didn't ring 2 times (switched off, not reachable…), or a Lead Manager put them here. Open one and Move it to someone to try again; if they enquire again, they start over by themselves.", empty: 'No junk leads', icon: '🚮' },
};

function noteOf(stage: ListStage, l: StageLeadsData['leads'][number]) {
  if (stage === 'Poll Sent') return <>Poll sent{l.pollSentAt ? ` ${fmtWhen(l.pollSentAt)}` : ''}{l.nextAt ? ` · status due ${fmtWhen(l.nextAt)}` : ''}</>;
  if (stage === 'Interested') return <>{l.interestedAt ? `Interested since ${fmtWhen(l.interestedAt)}` : 'Interested'}{l.nextAt ? ` · pays ${fmtWhen(l.nextAt)}` : ' · no payment date'}</>;
  // (the reason: didn't come for the visit, or no response after the P2P meeting)
  if (stage === 'No Response') return <>{l.reason ? <b className="text-gray-700">{l.reason}</b> : <>Poll sent{l.pollSentAt ? ` ${fmtWhen(l.pollSentAt)}` : ''}</>}{l.lastCallAt ? ` · last call ${fmtWhen(l.lastCallAt)}` : ''}</>;
  return <><b className="text-gray-700">{l.reason || 'No reason given'}</b>{l.closedAt ? ` · ${fmtWhen(l.closedAt)}` : ''}</>;
}

const CrmStageLeads: React.FC<{ auth: CrmAuth; stages: ListStage[]; isManager: boolean; embedded?: boolean }> = ({ auth, stages, isManager, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const [stage, setStage] = useState<ListStage>(stages[0]);
  const [person, setPerson] = useState('');
  const [reason, setReason] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState<StageLeadsData | null>(null);
  const [rows, setRows] = useState<StageLeadsData['leads']>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const r = await crm.stageLeads(auth, { stage, salesPersonId: person, reason, q: query, page });
      setData(r);
      setRows(prev => (page === 1 ? r.leads : [...prev, ...r.leads]));
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [auth, stage, person, reason, query]);
  useEffect(() => { load(); }, [load]);

  const about = ABOUT[stage];
  const pick = (s: ListStage) => { setStage(s); setReason(''); setRows([]); setData(null); };
  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title={about.title} sub={embedded && stages.length > 1 ? undefined : about.sub} />
      {stages.length > 1 && (
        <div className="space-y-1.5">
          <div className="flex gap-1 bg-white rounded-xl border border-gray-100 p-1 w-fit max-w-full overflow-x-auto" role="tablist" aria-label="Which leads">
            {stages.map(s => (
              <button key={s} role="tab" aria-selected={stage === s} onClick={() => pick(s)} className="px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap"
                style={stage === s ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
                {ABOUT[s].icon} {ABOUT[s].title}
              </button>
            ))}
          </div>
          {embedded && <p className="text-xs text-gray-500 px-1">{about.sub}</p>}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, number, course…" className={`${inputCls} sm:max-w-xs`} aria-label="Search" />
        {isManager && data && stage !== 'Junk Lead' && (
          <select value={person} onChange={e => setPerson(e.target.value)} className={`${inputCls} sm:max-w-xs`} aria-label="Salesperson">
            <option value="">Everyone · {data.owners.reduce((s, o) => s + o.n, 0)}</option>
            {data.owners.map(o => <option key={o.id || 'none'} value={o.id || 'none'}>{o.name} · {o.n}</option>)}
          </select>
        )}
      </div>
      {data && data.reasons.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {[{ reason: '', n: data.reasons.reduce((s, r) => s + r.n, 0) }, ...data.reasons].map(r => (
            <button key={r.reason || 'all'} onClick={() => setReason(r.reason === '—' ? '' : r.reason)} className="px-3 py-1.5 rounded-full text-xs font-bold border"
              style={reason === r.reason ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }}>
              {r.reason || 'Every reason'} <span className="opacity-60">{r.n}</span>
            </button>
          ))}
        </div>
      )}
      {!data || !me ? <Spinner /> : rows.length === 0 ? (
        <Empty icon={about.icon} title={query || reason || person ? 'No leads match' : about.empty} />
      ) : (
        <>
          <p className="text-xs text-gray-400 px-1">{data.total} lead{data.total === 1 ? '' : 's'}</p>
          <div className="space-y-2">
            {rows.map(l => (
              <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} showOwner={isManager} maxOwners={data.maxOwners}
                extra={<p className="text-[11px] text-gray-500 mt-0.5 truncate">{noteOf(stage, l)}</p>} />
            ))}
          </div>
          {data.page < data.pages && (
            <div className="text-center">
              <button onClick={() => load(data.page + 1)} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">{loading ? 'Loading…' : 'Load more'}</button>
            </div>
          )}
        </>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={() => load()} />}
    </div>
  );
};

export default CrmStageLeads;
